import {
	DownloadSpecification,
	UploadInfo,
	UploadRequestParams,
	UploadSpecification,
	UserRole,
	ZoneService,
} from '@citrite/sf-api';
import { ProjectsNetworkError } from '../ProjectsNetworkError';
import {
	BulkAddProjectUserPayload,
	ClientDelegationPayload,
	EntitlementResult,
	ProjectDto,
	ProjectFileDto,
	ProjectLifeCycleStage,
	ProjectStatus,
	ProjectUserDto,
	ProjectWithTagsDto,
	QueryProjectsByTagsPayload,
	ResourceEntityType,
	ShareFileUserDto,
	SortDirection,
	SortField,
	SystemIdentifier,
	TagValue,
} from '../types';
import { ProjectDataProvider } from './ProjectDataProvider';

interface Configuration {
	origin: string;
	apiPath: string;
}

const configuration: Configuration = {
	origin: window.origin,
	apiPath: '/io/projects/',
};

export function configureHttp(configure: (current: Configuration) => void) {
	configure(configuration);
}

function createRequest(path: string, init: RequestInit): Request {
	const url = `${configuration.origin}${configuration.apiPath}${path}`;
	init.headers = { ...init.headers, 'X-BFF-CSRF': 'true' };
	return new Request(url, init);
}

export const DefaultProjectDataProvider: ProjectDataProvider = {
	async createProject(
		project: Pick<
			ProjectDto,
			'name' | 'description' | 'enableFileBrowser' | 'fileBrowserOptions'
		>
	): Promise<ProjectDto> {
		const request = createRequest('projects', {
			method: 'POST',
			body: JSON.stringify(project),
			headers: {
				'content-type': 'application/json',
			},
		});
		const response = await fetchOrThrowErrorFromResponse(request, {
			expectedStatus: 201,
			defaultErrorMessage: 'Unable to create project',
		});
		const createdProject = await response.json();
		return createdProject as ProjectDto;
	},

	async getPagedProjects({
		projectStatus,
		sortDirection,
		sortField,
		skip,
		limit,
		continuationToken,
		searchText,
		tags,
		tagOperator,
		...queryParams
	}) {
		const params = new URLSearchParams({ ...queryParams });
		projectStatus && params.append('projectStatus', projectStatus.toString());
		skip && params.append('skip', skip.toString());
		limit && params.append('limit', limit.toString());
		sortField && params.append('sort.field', sortField);
		sortDirection && params.append('sort.direction', sortDirection);
		continuationToken && params.append('continuationToken', continuationToken);
		searchText && params.append('searchText', searchText);

		// Always exclude actions to optimize api call
		params.append('includeActions', 'false');

		if (tags && tags.length > 0) {
			const compactTags = toCompactTagValues(tags);
			compactTags.forEach(tag => {
				params.append('tags', JSON.stringify(tag));
			});
			tagOperator && params.append('tags.op', tagOperator);
		}

		const request = createRequest(`projects/pagedlist?${params.toString()}`, {
			method: 'GET',
		});
		const response = await fetchOrThrowErrorFromResponse(request, {
			expectedStatus: 200,
			defaultErrorMessage: 'Unable to get projects with entitlements',
		});
		const text = await response.text();
		return JSON.parse(text, dateReviver);

		function toCompactTagValues(tagValues: TagValue[]): { n: string; v: string }[] {
			return tagValues.map(toCompactTagValue);
		}
		function toCompactTagValue(tagValue: TagValue): { n: string; v: string } {
			return {
				n: tagValue.name,
				v: tagValue.value,
			};
		}
	},

	async getEntitlements() {
		const request = createRequest('projects/entitlements/evaluate', {
			method: 'GET',
		});
		const response = await fetchOrThrowErrorFromResponse(request, {
			expectedStatus: 200,
			defaultErrorMessage: 'Unable to get projects with entitlements',
		});
		const text = await response.text();
		const responseObject = JSON.parse(text, dateReviver);
		return responseObject.results;
	},

	async getSortedProjectsWithEntitlements(
		containerRID: string,
		sortField: SortField,
		sortDirection: SortDirection,
		status?: ProjectStatus,
		lifeCycleStages?: ProjectLifeCycleStage[]
	) {
		const params = new URLSearchParams({
			'sort.field': sortField,
			'sort.direction': sortDirection,
			containerRID,
		});

		if (status) {
			params.append('status', status);
		}

		lifeCycleStages?.forEach(l => params.append('state', l));

		const request = createRequest(`projects/withentitlements?${params.toString()}`, {
			method: 'GET',
		});
		const response = await fetchOrThrowErrorFromResponse(request, {
			expectedStatus: 200,
			defaultErrorMessage: 'Unable to get projects with entitlements',
		});
		const text = await response.text();
		return JSON.parse(text, dateReviver) as {
			entitlementResults: EntitlementResult[];
			projects: ProjectDto[];
		};
	},

	async getSortedProjectsWithEntitlementsAndTags(
		containerRID: string,
		sortField: SortField,
		sortDirection: SortDirection,
		status?: ProjectStatus,
		lifeCycleStages?: ProjectLifeCycleStage[],
		userRIDs?: string[]
	) {
		const params = new URLSearchParams({
			'sort.field': sortField,
			'sort.direction': sortDirection,
			containerRID,
		});

		if (status) {
			params.append('status', status);
		}
		for (const userRID of userRIDs ?? []) {
			params.append('userRIDs', userRID);
		}

		lifeCycleStages?.forEach(l => params.append('state', l));

		const request = createRequest(
			`projects/withentitlementsandtags?${params.toString()}`,
			{
				method: 'GET',
			}
		);
		const response = await fetchOrThrowErrorFromResponse(request, {
			expectedStatus: 200,
			defaultErrorMessage: 'Unable to get projects with entitlements and tags',
		});
		const text = await response.text();
		return JSON.parse(text, dateReviver) as {
			entitlementResults: EntitlementResult[];
			projects: ProjectWithTagsDto[];
		};
	},

	async getSortedProjectsByContainerRID(
		containerRID: string,
		sortField: SortField,
		sortDirection: SortDirection,
		status?: ProjectStatus,
		lifeCycleStages?: ProjectLifeCycleStage[]
	) {
		const params = new URLSearchParams({
			'sort.field': sortField,
			'sort.direction': sortDirection,
			containerRID,
		});

		if (status) {
			params.append('status', status);
		}

		lifeCycleStages?.forEach(l => params.append('state', l));

		const request = createRequest(`projects?${params.toString()}`, { method: 'GET' });
		const response = await fetchOrThrowErrorFromResponse(request, {
			expectedStatus: 200,
			defaultErrorMessage: 'Unable to get projects',
		});
		const text = await response.text();
		return JSON.parse(text, dateReviver) as ProjectDto[];
	},

	async getDeletedProjects(containerRID: string) {
		const request = createRequest(`projects?state=Deleted&containerRID=${containerRID}`, {
			method: 'GET',
		});
		const response = await fetchOrThrowErrorFromResponse(request, {
			expectedStatus: 200,
			defaultErrorMessage: 'Unable to get deleted projects',
		});
		const text = await response.text();
		return JSON.parse(text, dateReviver) as ProjectDto[];
	},

	async getProjectById(projectId: string) {
		const request = createRequest(`projects/${projectId}`, { method: 'GET' });
		const response = await fetch(request);
		if (response.status !== 200) {
			throw new ProjectsNetworkError('Unable to get projects', response);
		}
		const text = await response.text();
		return JSON.parse(text, dateReviver) as ProjectDto;
	},

	async putProject(project: ProjectDto) {
		const request = createRequest(`projects/${project.id}`, {
			method: 'PUT',
			body: JSON.stringify(project),
			headers: {
				'content-type': 'application/json',
			},
		});
		const response = await fetch(request);
		if (response.status !== 200) {
			throw new ProjectsNetworkError('Unable to put project', response);
		}
		const text = await response.text();
		return JSON.parse(text, dateReviver) as ProjectDto;
	},

	async deleteProject(projectId: string) {
		const request = createRequest(`projects/${projectId}`, {
			method: 'DELETE',
		});
		await fetchOrThrowErrorFromResponse(request);
	},

	async getProjectUsers(projectId: string) {
		const request = await createRequest(`projects/${projectId}/users`, {
			method: 'GET',
		});
		const response = await fetch(request);
		if (response.status !== 200) {
			throw new ProjectsNetworkError('could not get project users', response);
		}
		const body: ProjectUserDto[] = await response.json();
		return body;
	},

	async addUsersToProject(projectId: string, payload: BulkAddProjectUserPayload) {
		const request = createRequest(`projects/${projectId}/users/bulkAdd`, {
			method: 'POST',
			body: JSON.stringify(payload),
			headers: {
				'content-type': 'application/json',
			},
		});
		const response = await fetch(request);
		if (response.status !== 204) {
			throw new ProjectsNetworkError('Unable to add users to project', response);
		}
	},

	async addDelegatingClient(projectId: string, payload: ClientDelegationPayload) {
		const request = createRequest(`projects/${projectId}/users/delegate`, {
			method: 'POST',
			body: JSON.stringify(payload),
			headers: {
				'content-type': 'application/json',
			},
		});
		const response = await fetch(request);
		if (response.status !== 201) {
			throw new ProjectsNetworkError('Unable to add new client to project', response);
		}
		const text = await response.text();
		return JSON.parse(text, dateReviver) as ShareFileUserDto;
	},

	async restoreProjectById(projectId: string) {
		const request = createRequest(`projects/${projectId}/restore`, {
			method: 'POST',
			body: JSON.stringify({}),
		});
		await fetchOrThrowErrorFromResponse(request, {
			expectedStatus: 204,
			defaultErrorMessage: 'Unable to restore projects',
		});
	},

	async getProjectFiles(projectId: string) {
		const request = createRequest(`projects/${projectId}/files`, { method: 'GET' });
		const response = await fetch(request);
		if (response.status !== 200) {
			throw new ProjectsNetworkError('Unable to get project files', response);
		}
		const text = await response.text();
		return JSON.parse(text, dateReviver) as ProjectFileDto[];
	},

	async getDeletedProjectFiles(projectId: string) {
		const request = createRequest(`projects/${projectId}/files?state=Deleted`, {
			method: 'GET',
		});
		const response = await fetch(request);
		if (response.status !== 200) {
			throw new ProjectsNetworkError('Unable to get deleted project files', response);
		}
		const text = await response.text();
		return JSON.parse(text, dateReviver) as ProjectFileDto[];
	},

	async deleteProjectFiles(projectId: string, fileIds: string[]) {
		const request = createRequest(`projects/${projectId}/files/delete`, {
			method: 'DELETE',
			body: JSON.stringify(fileIds),
			headers: {
				'content-type': 'application/json',
			},
		});
		await fetchOrThrowErrorFromResponse(request, {
			expectedStatus: 204,
			defaultErrorMessage: 'Unexpected status code in response from delete files request',
		});
	},

	async restoreProjectFiles(projectId: string, fileIds: string[]) {
		const request = createRequest(`projects/${projectId}/files/restore`, {
			method: 'POST',
			body: JSON.stringify(fileIds),
			headers: {
				'content-type': 'application/json',
			},
		});
		await fetchOrThrowErrorFromResponse(request, {
			expectedStatus: 204,
			defaultErrorMessage: 'Unexpected status code in response from delete files request',
		});
	},

	async getProjectFilesBulkDownloadUrl(projectId: string, fileIds: string[]) {
		const request = createRequest(`projects/${projectId}/files/download`, {
			method: 'POST',
			body: JSON.stringify(fileIds),
			headers: {
				'content-type': 'application/json',
			},
		});
		const response = await fetch(request);
		if (response.status !== 200) {
			throw new ProjectsNetworkError('Unable to download project files', response);
		}
		const text = await response.text();
		return JSON.parse(text, dateReviver) as DownloadSpecification;
	},

	async getRoleByUser(userId: string) {
		const request = createRequest(`sharefileusers/${userId}/role`, {
			method: 'GET',
		});

		const response = await fetch(request);
		if (response.status !== 200) {
			throw new ProjectsNetworkError('Unable to get user role', response);
		}
		const text = await response.text();
		return JSON.parse(text) as UserRole;
	},

	async uploadFile(
		projectId: string,
		requestingResourceId: string,
		uploadRequestParams: UploadRequestParams
	): Promise<UploadSpecification> {
		const searchParams = new URLSearchParams();
		searchParams.append('fileContainerResourceId', requestingResourceId);
		const request = createRequest(
			`projects/${projectId}/files/upload?${searchParams.toString()}`,
			{
				method: 'POST',
				body: JSON.stringify(uploadRequestParams),
				headers: {
					'content-type': 'application/json',
				},
			}
		);

		const response = await fetchOrThrowErrorFromResponse(request, {
			expectedStatus: 200,
			defaultErrorMessage: 'Unexpected status code encounter while starting upload',
		});

		const text = await response.text();
		const uploadSpec = JSON.parse(text, dateReviver) as UploadSpecification;
		return uploadSpec;
	},

	async uploadFileComplete(
		projectId: string,
		requestingResourceId: string,
		uploadInfo: UploadInfo
	) {
		const query = new URLSearchParams();
		query.append('fileSource', requestingResourceId);
		query.append('fileContainerResourceId', requestingResourceId);
		const request = createRequest(`projects/${projectId}/files/uploadcomplete?${query}`, {
			method: 'POST',
			body: JSON.stringify(uploadInfo),
			headers: {
				'content-type': 'application/json',
			},
		});
		const response = await fetch(request);
		if (response.status !== 201) {
			throw new ProjectsNetworkError('Unable to complete file upload', response);
		}
		const text = await response.text();
		const file = JSON.parse(text, dateReviver) as ProjectFileDto;
		return file;
	},

	async createClient(client: ShareFileUserDto) {
		const request = createRequest('sharefileusers/client', {
			method: 'POST',
			body: JSON.stringify(client),
			headers: {
				'content-type': 'application/json',
			},
		});
		const response = await fetchOrThrowErrorFromResponse(request, {
			expectedStatus: 201,
			defaultErrorMessage: 'Unable to create client',
		});
		const text = await response.text();
		return JSON.parse(text, dateReviver) as ShareFileUserDto;
	},

	async getProjectZoneServices(projectId: string): Promise<ZoneService> {
		const request = createRequest(`projects/${projectId}/zoneservice`, { method: 'GET' });
		const response = await fetchOrThrowErrorFromResponse(request, {
			expectedStatus: 200,
			defaultErrorMessage: 'Unable to get project zone services',
		});
		const text = await response.text();

		return convertZoneServicestoEnum(text.replace(/^"(.*)"$/, '$1'));
	},

	async getProjectContacts(rid: string): Promise<ShareFileUserDto[]> {
		const request = createRequest(`projects/contacts?resourceId=${rid}`, {
			method: 'GET',
		});
		const response = await fetchOrThrowErrorFromResponse(request, {
			expectedStatus: 200,
			defaultErrorMessage: 'Unable to get project contacts',
		});
		const text = await response.text();
		return JSON.parse(text, dateReviver) as ShareFileUserDto[];
	},

	async freezeProject(projectId: string) {
		const request = createRequest(`projects/${projectId}/freeze`, {
			method: 'POST',
		});
		await fetchOrThrowErrorFromResponse(request, {
			expectedStatus: 204,
			defaultErrorMessage: 'Unable to freeze project',
		});
	},

	async unfreezeProject(projectId: string) {
		const request = createRequest(`projects/${projectId}/unfreeze`, {
			method: 'POST',
		});
		await fetchOrThrowErrorFromResponse(request, {
			expectedStatus: 204,
			defaultErrorMessage: 'Unable to unfreeze project',
		});
	},

	async queryProjectsByTags(
		payload: QueryProjectsByTagsPayload,
		containerRIDPrefix: string,
		sortField?: SortField,
		sortDirection?: SortDirection,
		status?: ProjectStatus,
		lifeCycleStages?: ProjectLifeCycleStage[]
	) {
		const params = new URLSearchParams({
			containerRIDPrefix,
		});

		if (sortField) {
			params.append('sort.field', sortField);
		}

		if (sortDirection) {
			params.append('sort.direction', sortDirection);
		}

		if (status) {
			params.append('status', status);
		}

		lifeCycleStages?.forEach(l => params.append('state', l));

		const request = createRequest(
			`projects/querybytagswithentitlements?${params.toString()}`,
			{
				method: 'POST',
				body: JSON.stringify(payload),
				headers: {
					'content-type': 'application/json',
				},
			}
		);

		const response = await fetchOrThrowErrorFromResponse(request, {
			expectedStatus: 200,
			defaultErrorMessage: 'Unable to query projects by tags',
		});

		const text = await response.text();
		return JSON.parse(text, dateReviver);
	},
};

async function fetchOrThrowErrorFromResponse(
	request: Request,
	config: { expectedStatus?: number; defaultErrorMessage?: string } = {}
) {
	const response = await fetch(request);

	const isError =
		config.expectedStatus !== undefined
			? response.status !== config.expectedStatus
			: response.status > 399;

	if (isError) {
		let errorBody: any = {};
		if (response.headers.get('content-type')?.includes('application/json')) {
			errorBody = await response.json().catch(_ => ({}));
		}
		const message =
			errorBody?.message ??
			errorBody?.Message ??
			config.defaultErrorMessage ??
			'Unknown error';
		throw new ProjectsNetworkError(message, response, errorBody?.statusCode, errorBody);
	}

	return response;
}

function dateReviver(_key: string, value: string) {
	if (typeof value === 'string') {
		if (
			/^\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z)$/.test(
				value
			)
		) {
			return new Date(value);
		}
	}
	return value;
}

function convertZoneServicestoEnum(zoneServices: string): ZoneService {
	return zoneServices
		.split(', ')
		.map(zs => ZoneService[zs as keyof typeof ZoneService])
		.reduce((acc, item) => acc | item);
}

export function getNonSolutionProjectContainerRIDPrefix(accountId: string) {
	return `${SystemIdentifier}:${ResourceEntityType.account}-${accountId}/${ResourceEntityType.project}`;
}
